import type { Event, EventHint } from "@sentry/core";
import { reportToSentryMetricCounter } from "@remi/sentry";

const PERMISSION_ERROR_TYPE = "UNAUTHORIZED";
const FORBIDDEN_HTTP_STATUS = 403;

interface TrpcPermissionData {
  errorType: string | undefined;
  httpStatus: number | undefined;
  path: string | undefined;
}

const EMPTY: TrpcPermissionData = { errorType: undefined, httpStatus: undefined, path: undefined };

function readTrpcData(value: unknown): TrpcPermissionData {
  if (value === null || typeof value !== "object" || !("data" in value)) {
    return EMPTY;
  }
  const { data } = value;
  if (data === null || typeof data !== "object") {
    return EMPTY;
  }
  const errorType: unknown = "errorType" in data ? data.errorType : undefined;
  const httpStatus: unknown = "httpStatus" in data ? data.httpStatus : undefined;
  const path: unknown = "path" in data ? data.path : undefined;
  return {
    errorType: typeof errorType === "string" ? errorType : undefined,
    httpStatus: typeof httpStatus === "number" ? httpStatus : undefined,
    path: typeof path === "string" && path.length > 0 ? path : undefined,
  };
}

function resolveTrpcData(exception: unknown): TrpcPermissionData {
  const direct = readTrpcData(exception);
  if (direct.path !== undefined) {
    return direct;
  }
  return readTrpcData(exception instanceof Error ? exception.cause : undefined);
}

// Sentry's default JS grouping keys on the minified chunk filename, which rotates every deploy, so
// one authorization defect mints a fresh unmuted issue repeatedly. errorType/httpStatus/path come
// from the errorFormatter in packages/di-pattern/domain-api/src/trpc.ts.
export function fingerprintTrpcPermissionError<T extends Event>(event: T, hint: EventHint): T {
  try {
    const { errorType, httpStatus, path } = resolveTrpcData(hint.originalException);
    if (path === undefined) {
      return event;
    }
    const isPermissionError = errorType === PERMISSION_ERROR_TYPE || httpStatus === FORBIDDEN_HTTP_STATUS;
    if (!isPermissionError) {
      return event;
    }
    event.tags = { ...event.tags, "trpc.path": path };
    reportToSentryMetricCounter("trpc.client.permission-error-fingerprinted", 1);
  } catch {
    // chainBeforeSend has no try/catch: a throw here would kill every later handler
  }

  return event;
}
